/**
 * SearchSpring Recommendations Controller
 *
 * Orchestrator for SS-powered product recommendation sections. Handles:
 * - Fetch from SS Recommend API (different endpoint from search)
 * - Response normalization (recommend API has different structure)
 * - Card rendering via shared renderer (ss-client.js)
 * - Grid and carousel layout (matching Horizon's product-recommendations patterns)
 * - Beacon tracking (module='recommendations')
 * - GTM dataLayer events
 * - lastViewed tracking for homepage personalization
 *
 * One controller instance per ss-recommend-config block on the page.
 * Supports PDP (similar profile) and homepage (personalized profile) placements.
 *
 * Depends on (all loaded as defer before this module):
 * - ss-client.js: renderProductCard, renderProductGrid, createSSClient
 * - ss-beacon.js: SSBeacon class
 * - ss-gtm.js: SSGtm class
 */

// SVG icons for carousel arrows (same as Horizon's icon-arrow.svg and icon-caret.svg)
const ARROW_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width)" vector-effect="non-scaling-stroke" d="M4.25 10h11.5m0 0-4-4m4 4-4 4"/></svg>';
const CARET_SVG = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/></svg>';

class SSRecommendationsController {
  #config;
  #client;
  #lastProducts = null;

  constructor(configEl) {
    try {
      this.#config = JSON.parse(configEl.textContent);
    } catch {
      console.error('[SS Recommend] Failed to parse config');
      return;
    }

    if (!this.#config.enabled || !this.#config.siteId) {
      this.#showError('SearchSpring not configured');
      return;
    }

    this.#client = window.createSSClient({ siteId: this.#config.siteId });
  }

  async init() {
    if (!this.#client) return;

    if (!this.#config.profileTag) {
      this.#showError('SS Profile Tag is not set. Enter a profile tag in the section settings.');
      return;
    }

    const response = await this.#fetchRecommendations();

    if (!response) {
      this.#showError();
      return;
    }

    const products = this.#normalizeProducts(response);
    // Extract responseId from the first profile result
    const responseId = response[0]?.responseId || '';

    if (products.length === 0) {
      this.#hideSection();
      return;
    }

    this.#lastProducts = products;
    this.#renderResults(products);
    this.#initTracking(products, responseId);
    this.#trackLastViewed();
  }

  // ---------------------------------------------------------------------------
  // API
  // ---------------------------------------------------------------------------

  async #fetchRecommendations() {
    const { siteId, profileTag, productId, limit, shopper } = this.#config;

    const url = new URL(`https://${siteId}.a.searchspring.io/boost/${siteId}/recommend`);
    url.searchParams.set('tags', profileTag);
    url.searchParams.set('limits', limit || 4);
    url.searchParams.set('siteId', siteId);
    url.searchParams.set('beacon', 'true');

    if (productId) url.searchParams.set('products', productId);
    if (shopper) url.searchParams.set('shopper', shopper);

    // Reuse SS identity from storage (created by SSClient constructor)
    const userId = localStorage.getItem('ss-userId') || '';
    const sessionId = sessionStorage.getItem('ss-sessionId') || '';
    if (userId) url.searchParams.set('userId', userId);
    if (sessionId) url.searchParams.set('sessionId', sessionId);

    // Pass lastViewed for homepage personalization
    const lastViewed = sessionStorage.getItem('ss-lastViewed');
    if (lastViewed) {
      try {
        const ids = JSON.parse(lastViewed);
        if (Array.isArray(ids) && ids.length > 0) {
          url.searchParams.set('lastViewed', ids.join(','));
        }
      } catch { /* ignore */ }
    }

    // Try with retry
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000);

        const res = await fetch(url, { signal: controller.signal });
        clearTimeout(timeout);

        if (!res.ok) throw new Error(`SS Recommend ${res.status}`);
        return await res.json();
      } catch (err) {
        if (attempt === 0) {
          console.warn('[SS Recommend] Retrying after error:', err.message);
          continue;
        }
        console.error('[SS Recommend]', err);
        return null;
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Response normalization
  // ---------------------------------------------------------------------------

  #normalizeProducts(apiResponse) {
    if (!Array.isArray(apiResponse)) return [];

    const allProducts = [];

    for (const profile of apiResponse) {
      if (!profile.results) continue;
      for (const item of profile.results) {
        const core = item.mappings?.core || {};
        const attrs = item.attributes || {};
        allProducts.push({
          ...core,
          ...attrs,
          image: core.imageUrl || core.thumbnailImageUrl || '',
          uid: core.uid || item.id,
          price: String(core.price || ''),
          msrp: String(core.msrp || ''),
        });
      }
    }

    return allProducts.slice(0, this.#config.limit);
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  #renderResults(products) {
    const skeletonEl = document.getElementById(`ss-recommend-skeleton-${this.#config.sectionId}`);
    if (!skeletonEl) return;

    const cardConfig = {
      ...this.#config,
      itemTag: 'div',
      itemClass: 'resource-list__item',
    };

    const { layoutType, carouselOnMobile } = this.#config;
    let html = '';

    if (layoutType === 'carousel') {
      // Pure carousel
      html = `<div class="resource-list force-full-width ss-recommend-results">${this.#buildCarouselHTML(products, cardConfig)}</div>`;
    } else if (carouselOnMobile) {
      // Grid on desktop, carousel on mobile
      const gridHTML = window.renderProductGrid(products, cardConfig);
      html =
        `<div class="resource-list hidden--mobile resource-list--grid ss-recommend-results">${gridHTML}</div>` +
        `<div class="resource-list hidden--desktop force-full-width ss-recommend-results" style="--resource-list-gap: ${this.#config.columnsGap}px; --column-count: ${this.#config.columns};">${this.#buildCarouselHTML(products, cardConfig)}</div>`;
    } else {
      // Pure grid
      const gridHTML = window.renderProductGrid(products, cardConfig);
      html = `<div class="resource-list resource-list--grid ss-recommend-results">${gridHTML}</div>`;
    }

    // Insert as direct children of .section (after skeleton) so force-full-width works
    skeletonEl.insertAdjacentHTML('afterend', html);
    skeletonEl.hidden = true;

    this.#updateOuterArrowVisibility();
  }

  // Horizon's slideshow.js sets `next.disabled` synchronously at init using
  // `visibleSlides.length`, but visibleSlides is populated async by an
  // IntersectionObserver — so on first paint the next button thinks there's
  // more to scroll when there isn't, becomes visible on hover, then collapses
  // to display:none after the first click forces a re-evaluation.
  //
  // This check side-steps that race by toggling a class on the slideshow when
  // the scroller has no real overflow, and CSS handles hiding arrows. Once
  // overflow does exist, we drop the class and let Horizon's natural disabled
  // logic take over.
  #updateOuterArrowVisibility() {
    const sectionEl = document.getElementById(`ss-recommend-section-${this.#config.sectionId}`);
    if (!sectionEl) return;

    // carouselOnMobile produces a second slideshow that's only visible on
    // mobile; querySelectorAll covers both.
    const slideshows = sectionEl.querySelectorAll('slideshow-component.resource-list__carousel');

    for (const slideshow of slideshows) {
      const scroller = slideshow.querySelector('slideshow-slides');
      if (!scroller) continue;

      const update = () => {
        // When an ancestor is display:none, scrollWidth and offsetWidth both
        // read 0 — hasOverflow=false is harmless there since the whole tree
        // is hidden. ResizeObserver re-fires once the ancestor unhides.
        const hasOverflow = scroller.scrollWidth > scroller.offsetWidth;
        slideshow.classList.toggle('ss-no-overflow', !hasOverflow);
      };

      // Double rAF gives the custom element time to upgrade and lay out
      // before we measure box dimensions.
      requestAnimationFrame(() => requestAnimationFrame(update));

      new ResizeObserver(update).observe(scroller);
    }
  }

  // ---------------------------------------------------------------------------
  // Carousel DOM builder
  // ---------------------------------------------------------------------------

  #buildCarouselHTML(products, cardConfig) {
    const { sectionWidth, iconsStyle, iconsShape } = this.#config;

    const gutters = sectionWidth === 'page-width' ? 'start end' : '';
    const gutterStyle = sectionWidth === 'page-width'
      ? '--gutter-slide-width: var(--util-page-margin-offset);'
      : '--gutter-slide-width: 0px;';

    // Build timeline scope (required by slideshow-component)
    const timelineScope = products.map((_, i) => `--slide-${i}`).join(', ');

    // Build slide elements
    const slidesHTML = products.map((product, i) => {
      const cardHTML = window.renderProductCard(product, cardConfig);
      const id = product.uid || product.id || '';
      return (
        `<slideshow-slide ref="slides[]" aria-hidden="${i === 0 ? 'false' : 'true'}" ` +
        `class="resource-list__slide" style="--slideshow-timeline: --slide-${i};">` +
        `<div class="resource-list__item" data-product-id="${this.#escapeAttr(id)}">${cardHTML}</div>` +
        `</slideshow-slide>`
      );
    }).join('');

    // Build arrows (if enabled)
    const showArrows = iconsStyle !== 'none';
    const arrowsHTML = showArrows
      ? `<slideshow-arrows position="center">${this.#buildArrowButton('previous', iconsStyle, iconsShape)}${this.#buildArrowButton('next', iconsStyle, iconsShape)}</slideshow-arrows>`
      : '';

    return (
      `<div class="resource-list__carousel" style="${gutterStyle} --slide-width-max: 300px;">` +
      `<slideshow-component class="resource-list__carousel" style="--slideshow-timeline: ${timelineScope};" initial-slide="0">` +
      `<slideshow-container ref="slideshowContainer">` +
      arrowsHTML +
      `<slideshow-slides tabindex="-1" ref="scroller"${gutters ? ` gutters="${gutters}"` : ''}>` +
      slidesHTML +
      `</slideshow-slides>` +
      `</slideshow-container>` +
      `</slideshow-component>` +
      `</div>`
    );
  }

  #buildArrowButton(action, iconStyle, iconShape) {
    const style = iconStyle || 'arrow';
    const shape = iconShape || 'none';
    const isLarge = style.includes('large');
    const iconName = style.includes('chevron') ? 'caret' : 'arrow';
    const svgHTML = iconName === 'caret' ? CARET_SVG : ARROW_SVG;

    const classes = [
      'slideshow-control',
      `slideshow-control--${action}`,
      isLarge ? 'slideshow-control--large' : null,
      shape !== 'none' ? `slideshow-control--shape-${shape}` : null,
      `slideshow-control--style-${style}`,
      'button', 'button-unstyled', 'button-unstyled--transparent',
      action === 'previous' ? 'flip-x' : null,
    ].filter(Boolean).join(' ');

    const label = action === 'previous' ? 'Previous' : 'Next';

    return (
      `<button class="${classes}" aria-label="${label}" on:click="/${action}" ref="${action}">` +
      `<span class="svg-wrapper icon-${iconName}">${svgHTML}</span>` +
      `</button>`
    );
  }

  // ---------------------------------------------------------------------------
  // Tracking
  // ---------------------------------------------------------------------------

  #initTracking(products, responseId) {
    const gridSelector = `#ss-recommend-section-${this.#config.sectionId}`;

    // Initialize beacon and GTM BEFORE dispatching the event,
    // so their ss:results-rendered listeners are in place when it fires.
    if (window.SSBeacon) {
      const beacon = new window.SSBeacon({
        siteId: this.#config.siteId,
        pageLoadId: this.#client.pageLoadId,
        module: 'recommendations',
        // SS requires the profile tag on every recommendations event
        tag: this.#config.profileTag,
        gridSelector,
      });
      beacon.init();
    }

    if (window.SSGtm) {
      const gtm = new window.SSGtm({
        listName: `SS Recommendations: ${this.#config.profileTag}`,
        gridSelector,
        itemSelector: '.resource-list__item',
        responseGetter: () => ({ results: this.#lastProducts }),
      });
      gtm.init();
    }

    // Now dispatch — beacon, GTM, and siblings listeners are all ready
    document.dispatchEvent(
      new CustomEvent('ss:results-rendered', {
        detail: {
          response: { results: products, responseId },
          gridSelector,
          storefrontToken: this.#config.storefrontToken,
          swatchCountDesktop: this.#config.swatchCountDesktop,
          swatchCountMobile: this.#config.swatchCountMobile,
        },
      })
    );
  }

  #trackLastViewed() {
    if (!this.#config.productId) return;

    try {
      const key = 'ss-lastViewed';
      const existing = JSON.parse(sessionStorage.getItem(key) || '[]');
      const productIdStr = String(this.#config.productId);
      if (!existing.includes(productIdStr)) {
        existing.unshift(productIdStr);
        sessionStorage.setItem(key, JSON.stringify(existing.slice(0, 10)));
      }
    } catch { /* ignore */ }
  }

  // ---------------------------------------------------------------------------
  // States
  // ---------------------------------------------------------------------------

  #showError(message) {
    const skeletonEl = document.getElementById(`ss-recommend-skeleton-${this.#config.sectionId}`);
    const errorEl = document.getElementById(`ss-recommend-error-${this.#config.sectionId}`);

    if (skeletonEl) skeletonEl.hidden = true;
    if (errorEl) {
      const heading = message || 'Unable to load recommendations';
      errorEl.innerHTML =
        `<h2 class="main-collection-grid__empty-title h2">${heading}</h2>` +
        '<p>Please try <a href="javascript:window.location.reload()" class="main-collection-grid__empty-link">reloading the page</a>.</p>';
      errorEl.removeAttribute('hidden');
    }

    console.error('[SS Recommend]', message || 'API request failed');
  }

  #hideSection() {
    const sectionEl = document.getElementById(`ss-recommend-section-${this.#config.sectionId}`);
    const skeletonEl = document.getElementById(`ss-recommend-skeleton-${this.#config.sectionId}`);

    if (skeletonEl) skeletonEl.hidden = true;
    // Hide entire section when no products (no heading with zero products)
    if (sectionEl) sectionEl.style.display = 'none';
  }

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------

  #escapeAttr(str) {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}

// =============================================================================
// Initialization
// =============================================================================

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[id^="ss-recommend-config-"]').forEach((configEl) => {
    const controller = new SSRecommendationsController(configEl);
    controller.init();
  });
});
